import type { Locale } from '@infiltre/shared';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import { useT } from '@/shared/lib/i18n';
import { AdaptiveDialog } from './AdaptiveDialog';
import { BrandMark } from './BrandMark';
import { HandKicker } from './HandKicker';
import { LanguageRowList } from './LanguageRowList';
import { PhaseTag } from './PhaseTag';
import { Pin } from './Pin';

export type LanguagePickerSheetProps = {
  open: boolean;
  onClose: () => void;
  /** Controlled locale. When omitted, the sheet drives the global store. */
  value?: Locale;
  /** Controlled change handler. When omitted, the sheet calls `setLocale`. */
  onChange?: (l: Locale) => void;
};

/**
 * Standalone flag picker — mobile sheet / desktop modal — driven through
 * the dialog stack. Default behaviour writes to the global locale store;
 * callers (e.g. host settings) can pass `value` + `onChange` to scope
 * the change to their own state.
 */
export const LanguagePickerSheet = ({
  open,
  onClose,
  value,
  onChange,
}: LanguagePickerSheetProps) => {
  const t = useT();
  const storeLocale = useUiStore((s) => s.locale);
  const setStoreLocale = useUiStore((s) => s.setLocale);
  const current = value ?? storeLocale;

  const handleSelect = (next: Locale): void => {
    if (onChange) onChange(next);
    else setStoreLocale(next);
    onClose();
  };

  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      ariaLabel={t('settings.section.language')}
      closeLabel={t('settings.close')}
      surface="bare"
      hideHandle
      desktopMaxWidth={560}
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t('settings.section.language')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t('settings.section.language')}
          </BrandMark>
          <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
            — {t('settings.language.subtitle')}
          </HandKicker>
        </header>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          <LanguageRowList value={current} onSelect={handleSelect} />
        </div>
      </div>
    </AdaptiveDialog>
  );
};
